import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { axiosPatch } from "api";
import MobileFooterLayout from "components/layout/MobileFooterLayout";
import PageLayout from "components/layout/PageLayout";
import { ORDER_STATUSES, ROUTES, STATUS_CODES } from "constants/index";
import { getOrderStatusUrl, ORDERS_URL } from "constants/urls";
import {
  buildOrderErrorMessage,
  canStopOrder,
  getOrderStatusChipColor,
  getOrderStatusLabel,
} from "helpers/orders";
import {
  buildOperatorPreparationPayload,
  getOperatorArticleObservation,
  MICROCONTROL_CLIENT_ID,
} from "helpers/operatorOrders";
import useCrud from "hooks/useCrud";
import { useToast } from "hooks/useToast";
import { Order, UpdateOrderPayload } from "types";
import { typographyStyles } from "constants/styles";

export default function OperatorOrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showSnackBar } = useToast();
  const { fetchData } = useCrud<Order>(ORDERS_URL, { disableAutoFetch: true });
  const fetchCurrent = useRef(fetchData);
  fetchCurrent.current = fetchData;
  const initialized = useRef<string | null>(null);
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [observations, setObservations] = useState<Record<string, string>>({});
  const [generalObservation, setGeneralObservation] = useState("");

  const load = useCallback(async () => {
    if (!id || !/^\d+$/.test(id)) {
      setLoading(false);
      setOrder(null);
      setError(true);
      return;
    }
    setLoading(true);
    const response = await fetchCurrent.current(id, true);
    if (response.status !== STATUS_CODES.OK || !response.data) {
      setOrder(null);
      setError(true);
      setLoading(false);
      showSnackBar("No se pudo cargar el pedido.", "error");
      return;
    }

    const next = response.data as Order;
    if (next.client_id !== MICROCONTROL_CLIENT_ID) {
      setOrder(null);
      setError(true);
      setLoading(false);
      return;
    }

    setOrder(next);
    setError(false);
    setGeneralObservation(next.observation ?? "");
    setChecked({});
    setObservations(Object.fromEntries(next.articles.map((article) => [
      String(article.id),
      getOperatorArticleObservation(article),
    ])));
    setSaved(false);
    setLoading(false);
  }, [id, showSnackBar]);

  useEffect(() => {
    if (initialized.current === id) return;
    initialized.current = id ?? null;
    void load();
  }, [id, load]);

  const status = order?.movement_order?.order_status;
  const articles = order?.articles ?? [];
  const completed = articles.filter(
    (article) => checked[String(article.id)],
  ).length;
  const allCompleted = articles.length > 0 && completed === articles.length;
  const trimmedGeneralObservation = generalObservation.trim();
  const statusReason =
    trimmedGeneralObservation &&
    trimmedGeneralObservation !== order?.observation?.trim()
      ? trimmedGeneralObservation
      : null;

  const begin = async () => {
    if (!order?.id || busy || status !== ORDER_STATUSES.INGRESADO) return;
    setBusy(true);
    const response = await axiosPatch(
      getOrderStatusUrl(order.id),
      {
        status: ORDER_STATUSES.EN_PREPARACION,
        reason: statusReason,
      },
      true,
    );
    if (response.status === STATUS_CODES.OK) {
      setGeneralObservation("");
      await load();
      showSnackBar("Pedido en preparación.", "success");
    } else {
      showSnackBar(buildOrderErrorMessage(response.data), "error");
    }
    setBusy(false);
  };

  const finish = async () => {
    if (
      !order?.id ||
      busy ||
      status !== ORDER_STATUSES.EN_PREPARACION ||
      !allCompleted
    )
      return;
    setBusy(true);
    if (!saved) {
      let payload: UpdateOrderPayload;
      try {
        payload = buildOperatorPreparationPayload(order, observations, generalObservation);
      } catch (error) {
        showSnackBar(error instanceof Error ? error.message : "No se pudo preparar el detalle del pedido.", "error");
        setBusy(false);
        return;
      }
      const saveResponse = await axiosPatch(
        `${ORDERS_URL}${order.id}`,
        payload,
        true,
      );
      if (saveResponse.status !== STATUS_CODES.OK) {
        showSnackBar(
          buildOrderErrorMessage(
            saveResponse.data,
            "No se pudo guardar la preparación.",
          ),
          "error",
        );
        setBusy(false);
        return;
      }
      setSaved(true);
    }
    const response = await axiosPatch(
      getOrderStatusUrl(order.id),
      {
        status: ORDER_STATUSES.PREPARADO,
        reason: statusReason,
      },
      true,
    );
    if (response.status === STATUS_CODES.OK) {
      showSnackBar("Pedido preparado.", "success");
      navigate(ROUTES.ORDERS);
    } else {
      showSnackBar(
        buildOrderErrorMessage(
          response.data,
          "Se guardaron los datos del pedido, pero no se pudo marcar preparado. Reintentá.",
        ),
        "error",
      );
    }
    setBusy(false);
  };

  if (loading) {
    return (
      <PageLayout pageTitle="Preparación de pedido" pageDesc="">
        <Stack spacing={2} sx={{ maxWidth: 760, mx: "auto" }}>
          <Typography role="status">Cargando pedido…</Typography>
        </Stack>
      </PageLayout>
    );
  }

  if (error || !order) {
    return (
      <PageLayout pageTitle="Preparación de pedido" pageDesc="">
        <Stack spacing={2} sx={{ maxWidth: 760, mx: "auto" }}>
          <Typography>No se pudo mostrar el pedido.</Typography>
          <Button onClick={() => void load()} sx={{ alignSelf: "flex-start" }}>
            Reintentar
          </Button>
        </Stack>
      </PageLayout>
    );
  }

  const isEditable = canStopOrder(order);

  return (
    <PageLayout
      pageTitle={
        <Stack spacing={0.5}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={typographyStyles.monospace}
          >
            Remito: {order.numero_remito || "Sin documento"}
          </Typography>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            flexWrap="wrap"
          >
            <Typography variant="h5" fontWeight="bold">
              Pedido #{order.id}
            </Typography>
            <Chip
              label={getOrderStatusLabel(status)}
              color={getOrderStatusChipColor(status)}
              size="small"
            />
          </Stack>
        </Stack>
      }
      pageDesc=""
    >
      <Stack
        spacing={2}
      >
        {!isEditable && (
          <Alert severity="info">
            El pedido ya no está disponible para preparación.
          </Alert>
        )}
        {order.observation && (
          <Typography variant="body2">
            Observación del pedido: {order.observation}
          </Typography>
        )}
        {articles.map((article) => {
          const key = String(article.id);
          const location = article.article_locations?.location?.code;
          const completionColor = (observations[key] ?? "").trim()
            ? "warning.main"
            : "success.main";
          return (
            <Card
              key={key}
              variant="outlined"
              sx={{
                p: 1,
                borderColor: checked[key] ? completionColor : undefined,
              }}
            >
              <Stack spacing={1}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!checked[key]}
                      disabled={
                        status !== ORDER_STATUSES.EN_PREPARACION ||
                        busy ||
                        saved
                      }
                      onChange={(event) =>
                        setChecked((value) => ({
                          ...value,
                          [key]: event.target.checked,
                        }))
                      }
                      sx={{
                        p: 0.5,
                        "& .MuiSvgIcon-root": { fontSize: 32 },
                        "&.Mui-checked, &.Mui-checked.Mui-disabled": {
                          color: completionColor,
                        },
                      }}
                    />
                  }
                  label={
                    <Typography
                      fontWeight={700}
                      sx={{ fontSize: 18, lineHeight: 1 }}
                    >
                      {article.code || article.article_id} ·{" "}
                      {article.description}
                    </Typography>
                  }
                />
                <Typography variant="body2">
                  Cantidad: {article.amount} · {article.amount_per_container}{" "}
                  por contenedor
                </Typography>
                <Typography variant="body2">
                  Ubicación: {location || "Sin ubicación"}
                </Typography>
                {article.lote && (
                  <Typography variant="body2">Lote: {article.lote}</Typography>
                )}
                <TextField
                  label="Observación del artículo"
                  value={observations[key] ?? ""}
                  onChange={(event) =>
                    setObservations((value) => ({
                      ...value,
                      [key]: event.target.value,
                    }))
                  }
                  disabled={
                    status !== ORDER_STATUSES.EN_PREPARACION || busy || saved
                  }
                  multiline
                  minRows={2}
                  fullWidth
                  size="small"
                />
              </Stack>
            </Card>
          );
        })}
        {articles.length === 0 && (
          <Typography>Este pedido no tiene artículos para preparar.</Typography>
        )}
        <Divider />
        <TextField
          label="Observación del pedido"
          value={generalObservation}
          onChange={(event) => setGeneralObservation(event.target.value)}
          disabled={status !== ORDER_STATUSES.EN_PREPARACION || busy || saved}
          multiline
          minRows={2}
          fullWidth
        />
      </Stack>
      {isEditable && (
        <MobileFooterLayout hideWhenKeyboardOpen mobileSpacerHeight="7rem">
          <Stack sx={{ maxWidth: 680, mx: "auto" }}>
            <Typography variant="body2" fontWeight={700}>
              {completed} de {articles.length} artículos preparados
            </Typography>
            <LinearProgress
              variant="determinate"
              value={articles.length ? (100 * completed) / articles.length : 0}
              aria-label="Progreso de preparación"
              sx={{
                height: 7,
                borderTopLeftRadius: 4,
                borderTopRightRadius: 4,
              }}
            />
            <Button
              variant="contained"
              size="large"
              fullWidth
              disabled={
                busy ||
                (status === ORDER_STATUSES.EN_PREPARACION && !allCompleted)
              }
              onClick={() =>
                void (status === ORDER_STATUSES.INGRESADO ? begin() : finish())
              }
              sx={{
                py: 1.5,
                fontWeight: 700,
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
              }}
            >
              {busy
                ? "Guardando…"
                : status === ORDER_STATUSES.INGRESADO
                  ? "Comenzar preparación"
                  : "Marcar preparado"}
            </Button>
            {saved && (
              <Box color="text.secondary" fontSize="0.75rem">
                Datos del pedido guardados. Falta confirmar el estado.
              </Box>
            )}
          </Stack>
        </MobileFooterLayout>
      )}
    </PageLayout>
  );
}
